/**
 * WinnerSlotCard — single-slot compact winner card for pages 4-10.
 *
 * Feb 2026 Winner Photo Slots dispatch. Renders ONE rank's winner
 * (photo, dog name, brand-purple rank badge, exact caption) on a
 * given public page.
 *
 * Props:
 *   * winnerRank (number, 4-10) — the rank whose slot this card
 *     represents. Rank 1-3 are aggregated by <FeaturedPluckStrip />
 *     on the homepage; do NOT use this component for ranks 1-3.
 *
 * Data source: fetches ONCE per mount from
 *   GET /api/public/featured-photos?slot_prefix=""
 * (empty prefix returns all populated slots — the response is
 * Cache-Control: max-age=60, so multiple cards mounted on the
 * same route share the browser cache after the first request).
 * Client-side filters to the row whose winner_rank matches.
 *
 * Empty-state: returns null (no DOM). This is the SHIP-DARK
 * contract — until the admin populates a slot, the card is
 * invisible everywhere. Once populated, the card appears in-page.
 *
 * Copy contract (hard constraints — tested):
 *   * Badge: "#N" for ranks 2-10 (no trophy). Rank 1 is handled by
 *     FeaturedPluckStrip which shows the trophy. Cards on this
 *     component only render ranks 4-10, so no trophy here.
 *   * Caption: hardcoded "Cutest Pluck Winner" (never the entry's
 *     free-form caption field — that could carry owner names or
 *     duration language).
 *   * No owner name rendered anywhere.
 *   * No date, no duration language, no temporal promises.
 *
 * Click behavior: entire card wraps a <Link to="/cutest-pluck">.
 * aria-label: "Cutest Pluck Winner: {dog_name} - Rank #{rank}".
 *
 * Family-L: Tailwind tokens only, no inline hex. Uses CSS
 * custom properties (--pp-purple, --pp-v3-bg, --pp-shadow, etc.).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

// Exact caption string. Hardcoded — never sourced from the entry
// record, which can carry free-form text (owner names, dates,
// promises we won't keep).
export const WINNER_SLOT_CAPTION = "Cutest Pluck Winner";

// Module-level in-memory cache so multiple mounted cards on the
// same route share one network call. Reset on page reload.
let _cachedRows = null;
let _pendingPromise = null;

async function _fetchAllSlots() {
  if (_cachedRows) return _cachedRows;
  if (_pendingPromise) return _pendingPromise;
  // Guard: in some jest test-mock configurations `api.get` returns
  // undefined (mock that only queues one response, then exhausts).
  // Fail-closed to the ship-dark contract rather than propagating a
  // TypeError up through React's passive-effect commit.
  const call = api.get("/public/featured-photos?slot_prefix=");
  if (!call || typeof call.then !== "function") {
    _cachedRows = [];
    return _cachedRows;
  }
  _pendingPromise = call
    .then((r) => {
      _cachedRows = r?.data?.featured_photos || [];
      return _cachedRows;
    })
    .catch(() => {
      _cachedRows = [];
      return _cachedRows;
    })
    .finally(() => {
      _pendingPromise = null;
    });
  return _pendingPromise;
}

// Test-only helper — reset the module cache between test cases so
// `mockResolvedValue` swaps take effect. Exported (not `_`-prefixed)
// so jest can call it directly.
export function _resetWinnerSlotCache() {
  _cachedRows = null;
  _pendingPromise = null;
}

export default function WinnerSlotCard({ winnerRank }) {
  const [row, setRow] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const rows = await _fetchAllSlots();
      if (cancelled) return;
      const match = rows.find((r) => r?.winner_rank === winnerRank);
      setRow(match || null);
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [winnerRank]);

  // While the initial fetch is in flight OR after it resolved
  // with no matching row: render nothing. The ship-dark contract.
  if (!loaded) return null;
  if (!row) return null;

  const dogName = String(row.dog_name || "").trim();
  const photoUrl = row.photo_url || "";
  const badgeText = `#${winnerRank}`;

  return (
    <section
      className="my-8"
      data-testid={`winner-slot-card-${winnerRank}`}
    >
      <div
        className="mx-auto max-w-sm overflow-hidden rounded-3xl"
        style={{
          background: "var(--pp-v3-surface)",
          boxShadow: "var(--pp-shadow)",
          border: "1px solid var(--pp-line)",
        }}
      >
        <Link
          to="/cutest-pluck"
          className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{ "--tw-ring-color": "var(--pp-purple)" }}
          aria-label={`Cutest Pluck Winner: ${dogName} — Rank #${winnerRank}`}
          data-testid={`winner-slot-card-${winnerRank}-link`}
        >
          <div className="relative aspect-square w-full">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={dogName}
                className="h-full w-full object-cover"
                loading="lazy"
                data-testid={`winner-slot-card-${winnerRank}-photo`}
              />
            ) : (
              <div
                className="h-full w-full"
                style={{ background: "var(--pp-v3-bg)" }}
              />
            )}
            <div
              className="absolute left-3 top-3 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider text-white"
              style={{ background: "var(--pp-purple)" }}
              data-testid={`winner-slot-card-${winnerRank}-badge`}
            >
              {badgeText}
            </div>
          </div>
          <div className="p-4">
            <p
              className="font-heading text-lg font-bold"
              style={{ color: "var(--pp-text)" }}
              data-testid={`winner-slot-card-${winnerRank}-name`}
            >
              {dogName}
            </p>
            <p
              className="mt-1 text-sm"
              style={{ color: "var(--pp-text-soft)" }}
              data-testid={`winner-slot-card-${winnerRank}-caption`}
            >
              {WINNER_SLOT_CAPTION}
            </p>
          </div>
        </Link>
      </div>
    </section>
  );
}
